import { useState, useEffect, useCallback } from "react";
import { Row, Form, Select, SelectOption, Button, Flex, Icon, Icons, Input } from "@ir/ui";
import EditDeductionDetailsCol from "./EditDeductionDetailsCol";
import { useDeductionEdit, useDeductionDrawer } from "@/presentation/deduction/hooks";
import { Deduction } from "@/core/entities/deduction";
import { DeductionStageProps } from "@/core/entities/deduction/deduction/DeductionStage";
import { IUpdateDeductionIntelligenceRepositoryParams } from "@/core/repositories/deduction";
import { DeductionEditMode } from "@/store/deduction/deduction";
import { useDeductionIntelligenceOptions } from "./hooks/useDeductionIntelligenceOptions";

interface EditDeductionIntelligenceProps {
  deduction: Deduction;
  onCancel?: () => void;
}

interface IEditDeductionIntelligenceForm {
  priority: SelectOption;
  validity: SelectOption;
}

const EditDeductionIntelligence = ({ deduction, onCancel }: EditDeductionIntelligenceProps) => {
  const [EditForm] = Form.useForm<IEditDeductionIntelligenceForm>();
  const { setHasAnyUnsavedChanges } = useDeductionDrawer();
  const {
    setDeductionIntelligence,
    patchDeductionIntelligence,
    isDeductionIntelligenceUpdating,
    editMode,
    setEditMode,
  } = useDeductionEdit();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const [isPredictedPriority, setIsPredictedPriority] = useState<boolean>(deduction.isPredictedPriority ?? false);

  const currentStageAndStatus = deduction?.stages?.find((stage: DeductionStageProps) => stage.isCurrent);
  const { priorityList, validityList, isPrioritiesLoading, isValiditiesLoading } = useDeductionIntelligenceOptions();

  useEffect(() => {
    if (deduction) {
      const priorityValue = EditForm.getFieldValue("priority");
      const validityValue = EditForm.getFieldValue("validity");
      if (!priorityValue) {
        EditForm.setFieldValue("priority", {
          value: deduction?.priorityId?.toString(),
          label: deduction?.priorityName,
        });
      }
      if (!validityValue) {
        EditForm.setFieldValue("validity", {
          value: deduction?.validityId?.toString(),
          label: deduction?.validityName,
        });
      }
    }
  }, [deduction, EditForm]);

  const handleCancel = useCallback(
    ({ preserveEditMode }: { preserveEditMode?: boolean } = {}) => {
      setHasAnyUnsavedChanges(false);
      onCancel?.();
      EditForm.resetFields();
      if (!preserveEditMode) {
        setEditMode(null);
      }
    },
    [EditForm, onCancel, setEditMode, setHasAnyUnsavedChanges]
  );

  useEffect(() => {
    if (editMode === DeductionEditMode.DETAILS) {
      handleCancel({ preserveEditMode: true });
    }
  }, [editMode, handleCancel]);

  const handleSubmit = async () => {
    setIsSubmitDisabled(true);

    const validateFields = await EditForm.validateFields({
      dirty: true,
    });

    const deductionIntelligence: IUpdateDeductionIntelligenceRepositoryParams = {
      deduction_id: deduction.id,
      stage_id: deduction?.status?.statusId ?? null,
      priority_id: validateFields.priority?.value ? Number.parseInt(validateFields.priority?.value) : null,
      validity_id: validateFields.validity?.value ? Number.parseInt(validateFields.validity?.value) : null,
      is_predicted_priority: isPredictedPriority,
    };

    setDeductionIntelligence(deductionIntelligence);

    const updatedDeduction = await patchDeductionIntelligence();

    if (updatedDeduction) {
      setHasAnyUnsavedChanges(false);
      onCancel?.();
    }

    setEditMode(null);
  };

  const handleValuesChange = (changedValues: Partial<IEditDeductionIntelligenceForm>) => {
    if (changedValues.priority) {
      setIsPredictedPriority(false);
    }
    setHasAnyUnsavedChanges(true);
  };

  return (
    <Form
      form={EditForm}
      className="editDeductionIntelligence"
      onFinish={handleSubmit}
      onValuesChange={handleValuesChange}
    >
      <Row className="flex-1">
        <EditDeductionDetailsCol>
          <Form.Item label="Stage">
            <Input
              size="small"
              isReadOnly
              isDisabled
              defaultValue={currentStageAndStatus?.stageName ?? ""}
            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol>
          <Form.Item label="Status">
            <Input
              size="small"
              isReadOnly
              isDisabled
              defaultValue={deduction?.status?.statusName ?? ""}
            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol>
          <Form.Item
            isList={true}
            name="priority"
            label="Priority"
            rules={[{ required: true, message: "Select priority from list" }]}
          >
            <Select
              placeholder="Select Priority"
              size="small"
              options={priorityList}
              isLoading={isPrioritiesLoading}
              isClearable={false}
            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol>
          <Form.Item
            isList={true}
            name="validity"
            label="Validity"
            rules={[{ required: true, message: "Select validity from list" }]}
          >
            <Select
              placeholder="Select Validity"
              size="small"
              options={validityList}
              isLoading={isValiditiesLoading}
              isClearable={false}
            />
          </Form.Item>
        </EditDeductionDetailsCol>
        <EditDeductionDetailsCol span={12} md={12} className="mb-0 mt-3">
          <Form.Item>
            <Flex justifyContent="end" className="custom-button-container">
              <Button
                size="medium"
                color="grey"
                variant="clear"
                onPress={handleCancel}
                isDisabled={isDeductionIntelligenceUpdating}
              >
                <Icon size="medium" name={Icons.Close} slot="start" />
                Cancel
              </Button>

              {!isPrioritiesLoading && !isValiditiesLoading && (
                <Button
                  size="medium"
                  variant="solid"
                  type="submit"
                  isDisabled={isSubmitDisabled || isDeductionIntelligenceUpdating}
                >
                  <Icon
                    size="medium"
                    name={isDeductionIntelligenceUpdating ? Icons.Loading : Icons.Check}
                    slot="start"
                    className={isDeductionIntelligenceUpdating ? "icon-fast-loading" : ""}
                  />
                  Save
                </Button>
              )}
            </Flex>
          </Form.Item>
        </EditDeductionDetailsCol>
      </Row>
    </Form>
  );
};

export default EditDeductionIntelligence;
